/**
 * Admin Control Plane API client (भाग E4).
 *
 * Talks ONLY to the isolated Admin API (`VITE_ADMIN_API_BASE_URL`, default
 * `http://127.0.0.1:9443` — `astraspace.admin_api.app`, a structurally
 * separate FastAPI process from the application API). Never `X-API-Key`,
 * which this dashboard never even has a field for.
 *
 * **Login redesign**: the P12 token+MFA-header pair is retired. Every
 * request now carries `X-Admin-Session-Token` — the one credential minted by
 * `/admin/auth/set-password` or `/admin/auth/password`, only reachable after
 * both a Google identity (restricted server-side to one allowed account) and
 * a password have been verified (`admin_api/routes/login.py`). The session
 * token lives only in `sessionStorage` (cleared when the tab closes) — this
 * is an admin console, not a normal app, and it is expected to run behind an
 * IP-allowlist/VPN boundary per `deploy/astraspace-admin.service`.
 *
 * `credentials: "include"` on every call: the pre-session steps
 * (`/admin/auth/pending`, `/admin/auth/set-password`, `/admin/auth/password`)
 * are authenticated by an httponly cookie the backend sets on its own origin,
 * not by anything this client can read or attach itself — the browser has to
 * be told to send it cross-origin.
 */

const BASE_URL =
  (import.meta.env.VITE_ADMIN_API_BASE_URL as string | undefined) ?? "http://127.0.0.1:9443";

const STORAGE_KEY = "astraspace-admin-session";

export interface AdminSession {
  sessionToken: string;
  expiresAt: string;
  adminRole: string;
  email: string;
}

export function loadSession(): AdminSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AdminSession) : null;
  } catch {
    return null;
  }
}

export function saveSession(session: AdminSession): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function isSessionExpired(session: AdminSession): boolean {
  return new Date(session.expiresAt).getTime() <= Date.now();
}

export class AdminApiError extends Error {
  status: number;
  code: string;
  /** The error body's `details` object, when the server sent one. */
  details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; params?: Record<string, string | undefined> } = {},
): Promise<T> {
  const session = loadSession();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (session) {
    headers["X-Admin-Session-Token"] = session.sessionToken;
  }

  let url = `${BASE_URL}${path}`;
  if (options.params) {
    const qs = new URLSearchParams(
      Object.entries(options.params).filter(([, v]) => v !== undefined) as [string, string][],
    ).toString();
    if (qs) url += `?${qs}`;
  }

  const res = await fetch(url, {
    method: options.method ?? "GET",
    headers,
    credentials: "include",
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) {
    let code = "unknown_error";
    let message = res.statusText;
    let details: Record<string, unknown> = {};
    try {
      const body = await res.json();
      code = body.code ?? code;
      message = body.message ?? message;
      if (body.details && typeof body.details === "object") details = body.details;
    } catch {
      // Non-JSON error body — keep the defaults above.
    }
    if (session && (res.status === 401 || code === "admin_session_expired")) {
      // Auto-logout on expiry/revocation — never leave a dead session
      // sitting in `sessionStorage` pretending to still be usable.
      clearSession();
    }
    throw new AdminApiError(res.status, code, message, details);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const adminApi = {
  get: <T>(path: string, params?: Record<string, string | undefined>) =>
    request<T>(path, { params }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// --- login-flow shapes (भाग E4 redesign) -----------------------------------

export interface PendingStatus {
  email: string;
  must_set_password: boolean;
}

export interface SessionIssued {
  session_token: string;
  expires_at: string;
  admin_role: string;
  email: string;
}

export function googleLoginUrl(): string {
  return `${BASE_URL}/admin/auth/login`;
}

// --- shared response shapes ----------------------------------------------

export interface SubsystemStatus {
  name: string;
  status: "green" | "yellow" | "red";
  evidence: string;
  recommended_action: string;
}

export interface OverviewResponse {
  project_count: number;
  user_count: number;
  object_count: number;
  active_api_key_count: number;
  total_bytes_stored: number;
  subsystems: SubsystemStatus[];
}

export interface UserRow {
  id: string;
  project_id: string;
  external_user_id: string;
  status: string;
  quota_bytes: number;
  used_bytes: number;
  /** What the platform enforces: `quota_bytes` clamped to the per-user cap.
   *  Shown instead of `quota_bytes` wherever they differ, because the stored
   *  number above the cap is never honoured. */
  effective_quota_bytes?: number | null;
  /** Present only on the response to a quota change. */
  quota_warnings?: string[];
}

export interface StorageOverview {
  by_tier: { tier: string; object_count: number; bytes_total: number }[];
  copy_states: { state: string; count: number }[];
  integrity: SubsystemStatus;
}

export interface SecurityCenterResponse {
  status: SubsystemStatus;
  blocked_devices: number;
  quarantined_sessions: number;
  revoked_sessions: number;
  open_incidents: {
    id: string;
    source: string;
    severity: string;
    status: string;
    reason: string;
    project_id: string | null;
  }[];
}

export interface RansomwareIncident {
  id: string;
  source: string;
  severity: string;
  status: string;
  reason: string;
  project_id: string | null;
  user_id: string | null;
}

export interface InfrastructureResponse {
  status: SubsystemStatus;
  counters: Record<string, number>;
  latency_avg_ms: Record<string, number>;
  latency_count: Record<string, number>;
}

/** PiVault, from its last signed heartbeat. `checks` share the Overview's
 *  status shape; `freshness` is the one that matters most, because a wedged
 *  box sends nothing and the last document would otherwise look healthy. */
export interface VaultStatusResponse {
  configured: boolean;
  status: "green" | "yellow" | "red";
  host: string | null;
  last_received_at: string | null;
  age_seconds: number | null;
  age_words: string | null;
  interval_seconds: number;
  checks: SubsystemStatus[];
  reports_24h: number;
  expected_24h: number;
  boots_7d: { boot_id: string; first_seen: string; last_seen: string; reports: number }[];
  /** The document exactly as the box sent it. */
  latest: Record<string, unknown> | null;
  durability_note: string;
}

export interface BackupRecoveryResponse {
  status: SubsystemStatus;
  provider_map: {
    provider: string;
    is_backup: boolean;
    state: string;
    count: number;
    /** Cloudflare R2 does not implement S3 Object Lock, so a backup copy held
     *  there cannot be immutable whatever state it reaches. Without showing this,
     *  "r2ia / backup / protected" reads as ransomware-proof and is not. */
    provider_enforces_object_lock: boolean;
    with_worm_until: number;
  }[];
  recent_drills: { id: string; outcome: string; source: string; detail: string }[];
}

export interface CostResponse {
  hot_bytes: number;
  cold_bytes: number;
  backup_bytes: number;
  hot_cost_inr: number;
  cold_cost_inr: number;
  backup_cost_inr: number;
  total_cost_inr: number;
  recommendations: {
    object_id: string;
    action: string;
    monthly_savings_inr: number;
    message: string;
  }[];
}

export interface AuditLogRow {
  id: string;
  seq: number;
  ts: string;
  actor: string;
  action: string;
  project_id: string | null;
  user_id: string | null;
  object_id: string | null;
  result: string;
  reason: string | null;
  request_id: string | null;
  prev_hash: string;
  curr_hash: string;
}

export interface ProjectRow {
  id: string;
  name: string;
  quota_bytes: number;
  used_bytes: number;
  frozen: boolean;
  default_privacy_class: string | null;
  quota_webhook_url: string;
  /** The attached storage-location policy; null = platform default. */
  storage_policy_id?: string | null;
  /** Present only on the response to a quota change. */
  quota_warnings?: string[];
}

/** Where a project's bytes may live (R2, B2, R2-IA, PiVault) — see
 *  `admin_api/routes/storage_policies.py`. */
export interface StoragePolicy {
  id: string;
  name: string;
  allowed_locations: string[];
  preferred_primary: string | null;
  require_backup_copy: boolean;
  offsite_copy_possible: boolean;
  tailnet_only_locations: string[];
  unavailable_locations: string[];
  acknowledgement_required: string[];
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  projects: number;
}

export interface StoragePolicyBody {
  name: string;
  allowed_locations: string[];
  preferred_primary: string | null;
  require_backup_copy: boolean;
  acknowledge: string[];
}

export interface StorageLocations {
  locations: string[];
  available_now: string[];
  tailnet_only: string[];
}

export function listStoragePolicies(): Promise<StoragePolicy[]> {
  return adminApi.get<StoragePolicy[]>("/api/v1/admin/storage-policies");
}

export function listStorageLocations(): Promise<StorageLocations> {
  return adminApi.get<StorageLocations>("/api/v1/admin/storage-locations");
}

export function createStoragePolicy(body: StoragePolicyBody): Promise<StoragePolicy> {
  return adminApi.post<StoragePolicy>("/api/v1/admin/storage-policies", body);
}

export function updateStoragePolicy(id: string, body: StoragePolicyBody): Promise<StoragePolicy> {
  return adminApi.put<StoragePolicy>(`/api/v1/admin/storage-policies/${id}`, body);
}

export function deleteStoragePolicy(id: string): Promise<void> {
  return adminApi.delete<void>(`/api/v1/admin/storage-policies/${id}`);
}

export function attachStoragePolicy(projectId: string, policyId: string | null): Promise<unknown> {
  return adminApi.put(`/api/v1/admin/projects/${projectId}/storage-policy`, {
    policy_id: policyId,
  });
}

/** An S3 credential as operators see it (`admin_api.routes.s3_credentials`).
 *  Never carries the secret. `usable` is false once the API key it was minted
 *  with is revoked, whatever its own status says. */
export interface S3CredentialRow {
  id: string;
  project_id: string;
  user_id: string;
  api_key_id: string;
  name: string;
  access_key_id: string;
  bucket: string;
  scopes: string[];
  status: string;
  usable: boolean;
  expires_at: string;
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
  revoked_by: string | null;
}

export function listProjectS3Credentials(projectId: string): Promise<S3CredentialRow[]> {
  return adminApi.get<S3CredentialRow[]>(`/api/v1/admin/projects/${projectId}/s3-credentials`);
}

export function revokeS3Credential(id: string): Promise<void> {
  return adminApi.post<void>(`/api/v1/admin/s3-credentials/${id}/revoke`);
}

export interface BreakGlassOutcome {
  action: string;
  detail: string;
  affected: number;
}

// --- provisioning: projects / users / keys ---------------------------------
//
// भाग E4 dashboard provisioning. Every quota the *admin* enters here is in
// GB in the UI and converted to bytes before it reaches the API — the
// backend (`AdminService`, `admin_api.routes.*`) only ever speaks bytes.
// `BYTES_PER_GB` matches the binary convention the rest of this codebase
// already uses for quotas (e.g. the 1 TiB default user quota is
// `1024 ** 4` bytes) — 1 GB here means 1024³ bytes, not 10⁹.

export const BYTES_PER_GB = 1024 ** 3;

export function gbToBytes(gb: number): number {
  return Math.round(gb * BYTES_PER_GB);
}

export function bytesToGb(bytes: number): number {
  return bytes / BYTES_PER_GB;
}

export interface ApiKeyIssuedResponse {
  api_key_id: string;
  project_id: string;
  api_key: string;
  scopes: string[];
  fingerprint: string;
  admin_role: string;
}

export interface ApiKeyMetaResponse {
  id: string;
  project_id: string;
  scopes: string[];
  status: string;
  fingerprint: string;
  admin_role: string;
  expires_at: string | null;
}

export function listProjects(): Promise<ProjectRow[]> {
  return adminApi.get<ProjectRow[]>("/api/v1/admin/projects");
}

export function createProject(name: string, quotaBytes: number): Promise<ProjectRow> {
  return adminApi.post<ProjectRow>("/api/v1/admin/projects", { name, quota_bytes: quotaBytes });
}

export function setProjectQuota(projectId: string, quotaBytes: number): Promise<ProjectRow> {
  return adminApi.put<ProjectRow>(`/api/v1/admin/projects/${projectId}/quota`, {
    quota_bytes: quotaBytes,
  });
}

export function createUser(
  projectId: string,
  externalUserId: string,
  quotaBytes: number | null,
): Promise<UserRow> {
  return adminApi.post<UserRow>("/api/v1/admin/users", {
    project_id: projectId,
    external_user_id: externalUserId,
    quota_bytes: quotaBytes,
  });
}

export function setUserQuota(userId: string, quotaBytes: number): Promise<UserRow> {
  return adminApi.put<UserRow>(`/api/v1/admin/users/${userId}/quota`, {
    quota_bytes: quotaBytes,
  });
}

export function listKeys(projectId?: string): Promise<ApiKeyMetaResponse[]> {
  return adminApi.get<ApiKeyMetaResponse[]>("/api/v1/admin/keys", { project_id: projectId });
}

export function issueKey(
  projectId: string,
  scopes: string[],
  adminRole = "super",
): Promise<ApiKeyIssuedResponse> {
  return adminApi.post<ApiKeyIssuedResponse>("/api/v1/admin/keys", {
    project_id: projectId,
    scopes,
    admin_role: adminRole,
  });
}

export function rotateKey(apiKeyId: string): Promise<ApiKeyIssuedResponse> {
  return adminApi.post<ApiKeyIssuedResponse>(`/api/v1/admin/keys/${apiKeyId}/rotate`);
}

export function revokeKey(apiKeyId: string): Promise<ApiKeyMetaResponse> {
  return adminApi.post<ApiKeyMetaResponse>(`/api/v1/admin/keys/${apiKeyId}/revoke`);
}
